import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { Dealer, DealersList } from '../../shared/typings/main';
import { getBrowserLocation, reverseGeocodeToZip } from '../../shared/utils/geolocation';
import { useMyVwContext } from '../../shared/hooks/use-my-vw-context';
import { useVehicle } from './VehicleContext';
import { useFeatureServices } from '../../shared/hooks/use-feature-services';

interface DealerContextType {
  dealersList: DealersList[];
  isLoadingDealers: boolean;
  isLoadingPreferredDealer: boolean;
  noDealersFound: boolean;
  userPreferredDealer?: Dealer;
  selectedDepartmentCode: string;
  baseUrl?: string;

  clearDealersList: () => void;
  clearPreferredDealer: () => void;
  fetchDealersListByZipCode: (zipCode: string) => Promise<void>;
  fetchPreferredDealer: (vehicleId: string, vin: string, newDealerid?: string) => Promise<void>;
  getUserLocation: () => Promise<string | null>;
  setSelectedDepartmentCode: (departmentCode: string) => void;
}

const DealerContext = createContext<DealerContextType | undefined>(undefined);

export const DealerProvider = ({ children }: { children: React.ReactNode }) => {
  const [dealersList, setDealersList] = useState<DealersList[]>([]);
  const [isLoadingDealers, setIsLoadingDealers] = useState<boolean>(false);
  // True only when a search succeeded and returned zero dealers (not on API/network errors)
  const [noDealersFound, setNoDealersFound] = useState<boolean>(false);
  const [isLoadingPreferredDealer, setIsLoadingPreferredDealer] = useState<boolean>(false);
  const [userPreferredDealer, setUserPreferredDealer] = useState<Dealer | undefined>(undefined);
  const [selectedDepartmentCode, setSelectedDepartmentCode] = useState<string>('');

  const { authService, baseUrl } = useMyVwContext();
  const { userPreferredVehicle } = useVehicle();
  const { 'locale-service': localeService } = useFeatureServices();
  const localeCode = `${localeService.language}-${localeService.countryCode}`;

  /**
   * Fetch dealers by ZIP code
   */
  const fetchDealersListByZipCode = useCallback(async (zipCode: string): Promise<void> => {
    setIsLoadingDealers(true);
    setNoDealersFound(false);
    try {
      if (!zipCode) return;

      const { getDealersByZipAndDistanceWithLimitAndCountry = {} } =
        await authService.getDealerListGqlRequestWithCountry(zipCode, localeCode);

      const dealers = getDealersByZipAndDistanceWithLimitAndCountry?.dealers?.dealerList || [];

      setDealersList(dealers);
      setNoDealersFound(dealers.length === 0);
    } catch (error) {
      console.error('Error fetching dealers list', error);
      setDealersList([]);
    } finally {
      setIsLoadingDealers(false);
    }
  }, [authService]);


  /**
   * Get user's browser location and return the zip code
   */
  const getUserLocation = useCallback(async (): Promise<string | null> => {
    try {
      setIsLoadingDealers(true);

      const coords = await getBrowserLocation();
      const result = await reverseGeocodeToZip(coords.lat, coords.lng);

      setIsLoadingDealers(false);
      return result.zipCode || null;
    } catch (error) {
      console.log('Error getting user location:', error);
      setIsLoadingDealers(false);
      return null;
    }
  }, []);

  /**
   * Fetch preferred dealer for a specific vehicle
   * @param vehicleId - Vehicle ID
   * @param vin - Vehicle VIN
   * @param newDealerId - Optional: Use this dealer ID instead of fetching from vehicle association
   *                      Useful when the vehicle-dealer association hasn't been updated yet (cache/replication lag)
   */
  const fetchPreferredDealer = useCallback(async (
    vehicleId: string,
    vin: string,
    newDealerid?: string
  ): Promise<void> => {
    setIsLoadingPreferredDealer(true);

    try {
      let dealerId = newDealerid;

      // Only fetch association if we don't have newDealerid
      if (!dealerId) {
        const { getUserPreferDealerInfo } =
          await authService.getUserPreferDealerBasicInfoGqlRequest(vehicleId, localeCode);

        if (!getUserPreferDealerInfo?.length) {
          setUserPreferredDealer(undefined);
          setIsLoadingPreferredDealer(false);
          return;
        }

        dealerId = getUserPreferDealerInfo[0].dealerId;
      }

      if (!dealerId) {
        setUserPreferredDealer(undefined);
        setIsLoadingPreferredDealer(false);
        return;
      }

      const vehicleInfo = localeService.countryCode === 'CA' ? vehicleId : vin;
      const [dealerInfoRes, serviceUrlRes] = await Promise.all([
        authService.getServiceURLsOfDealerGqlRequest(dealerId, localeCode),
        authService.getServiceSchedulingUrl(vehicleInfo, dealerId, localeCode),
      ]);

      const dealer = dealerInfoRes?.getDealerById;
      const serviceSchedulingUrl = serviceUrlRes?.getServiceSchedulingUrl;

      if (!dealer) {
        setUserPreferredDealer(undefined);
        setIsLoadingPreferredDealer(false);
        return;
      }

      const updatedDealer = {
        id: dealerId,
        ...dealer,
        ...(serviceSchedulingUrl ?? {}),
      };

      setUserPreferredDealer(updatedDealer);
    } catch (error) {
      console.error('Error fetching preferred dealer', error);
      setUserPreferredDealer(undefined);
    } finally {
      setIsLoadingPreferredDealer(false);
    }
  }, [authService]);

  /**
   * Clear dealers list
   */
  const clearDealersList = useCallback(() => {
    setDealersList([]);
    setNoDealersFound(false);
  }, []);

  /**
   * Clear preferred dealer - useful when switching to a vehicle without preferred dealer
   */
  const clearPreferredDealer = useCallback(() => {    
    setUserPreferredDealer(undefined);
    setSelectedDepartmentCode('');
  }, []);

  // Auto-select the first available department when dealer changes
  useEffect(() => {
    if (userPreferredDealer?.departments?.length) {
      // Find Service department first (prioritized), otherwise use the first available
      const serviceDept = userPreferredDealer.departments.find(
        dept => dept.description.toLowerCase().includes('service')
      );

      const defaultDept = serviceDept || userPreferredDealer.departments[0];

      // Only set if not already selected or if current selection is not valid
      const isCurrentSelectionValid = userPreferredDealer.departments.some(
        dept => dept.departmentCode === selectedDepartmentCode
      );

      if (!selectedDepartmentCode || !isCurrentSelectionValid) {
        setSelectedDepartmentCode(defaultDept.departmentCode);
      }
    } else {
      // Clear selection if no departments available
      setSelectedDepartmentCode('');
    }
  }, [userPreferredDealer]);

  // Fetch preferred dealer when vehicle changes
  useEffect(() => {
    if (userPreferredVehicle?.vehicleId && userPreferredVehicle?.vin) {
      fetchPreferredDealer(
        userPreferredVehicle.vehicleId,
        userPreferredVehicle.vin
      );
    }
  }, [userPreferredVehicle]);

  return (
    <DealerContext.Provider
      value={{
        clearDealersList,
        clearPreferredDealer,
        dealersList,
        fetchDealersListByZipCode,
        fetchPreferredDealer,
        getUserLocation,
        isLoadingDealers,
        isLoadingPreferredDealer,
        noDealersFound,
        selectedDepartmentCode,
        setSelectedDepartmentCode,
        userPreferredDealer,
        baseUrl
      }}
    >
      {children}
    </DealerContext.Provider>
  );
}

export const useDealer = () => {
  const context = useContext(DealerContext);

  if (!context) {
    throw new Error('useDealer must be used within DealerProvider');
  }

  return context;
};